import { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { Icon } from '@iconify/react'
import { supabase } from '../supabaseClient'
import { QRCodeSVG } from 'qrcode.react'
import { gerarPixCopiaCola, gerarTxId } from '../services/pixService'
import { baixarRecibo } from '../utils/pdfGenerator'

export default function PortalCliente() {
  const { token } = useParams()
  const [loading, setLoading] = useState(true)
  const [erro, setErro] = useState(null)
  const [dados, setDados] = useState(null)
  const [expandedId, setExpandedId] = useState(null)
  const [pixData, setPixData] = useState(null)
  const [pixCopied, setPixCopied] = useState(false)
  const [pagandoId, setPagandoId] = useState(null)
  const [mostrarPagos, setMostrarPagos] = useState(false)
  const [isDesktop, setIsDesktop] = useState(false)

  useEffect(() => {
    if (token) carregarDados()
  }, [token])

  useEffect(() => {
    const atualizarBreakpoint = () => {
      if (typeof window !== 'undefined') {
        setIsDesktop(window.innerWidth >= 960)
      }
    }
    atualizarBreakpoint()
    window.addEventListener('resize', atualizarBreakpoint)
    return () => window.removeEventListener('resize', atualizarBreakpoint)
  }, [])

  const carregarDados = async () => {
    try {
      setLoading(true)
      const { data, error } = await supabase.functions.invoke(`portal-dados?token=${token}`)

      if (error || !data) {
        setErro(error?.message || 'Erro ao carregar portal')
        setLoading(false)
        return
      }

      setDados(data)
      setLoading(false)
    } catch (error) {
      console.error('Erro:', error)
      setErro('Erro ao carregar dados. Tente novamente.')
      setLoading(false)
    }
  }

  const handlePagar = async (mensalidade) => {
    if (expandedId === mensalidade.id) {
      setExpandedId(null)
      setPixData(null)
      setPixCopied(false)
      return
    }

    if (dados.asaas_configurado && dados.metodo_pagamento === 'asaas_link') {
      setPagandoId(mensalidade.id)
      setExpandedId(mensalidade.id)
      setPixData(null)
      try {
        const { data, error } = await supabase.functions.invoke('portal-pagar', {
          body: { token, mensalidade_id: mensalidade.id }
        })

        if (error || !data) {
          alert(error?.message || 'Erro ao gerar pagamento')
          setExpandedId(null)
        } else if (data.success) {
          if (data.pix_copia_cola) {
            setPixData({
              pixCode: data.pix_copia_cola,
              qrImage: data.pix_qr_code || null,
              invoiceUrl: data.invoice_url
            })
          } else if (data.invoice_url) {
            setPixData({ invoiceUrl: data.invoice_url })
            window.open(data.invoice_url, '_blank')
          }
          await carregarDados()
        } else {
          alert(data.error || 'Erro ao gerar pagamento')
          setExpandedId(null)
        }
      } catch (error) {
        alert('Erro ao processar pagamento')
        setExpandedId(null)
      } finally {
        setPagandoId(null)
      }
      return
    }

    if (!dados.empresa.chave_pix) {
      alert('Chave PIX nao configurada. Entre em contato com o estabelecimento.')
      return
    }

    const pixCode = gerarPixCopiaCola({
      chavePix: dados.empresa.chave_pix,
      valor: parseFloat(mensalidade.valor),
      nomeRecebedor: dados.empresa.nome || 'Empresa',
      cidadeRecebedor: 'SAO PAULO',
      txid: gerarTxId(mensalidade.id)
    })

    setExpandedId(mensalidade.id)
    setPixData({ pixCode })
    setPixCopied(false)
  }

  const copiarPix = async () => {
    if (!pixData?.pixCode) return
    try {
      await navigator.clipboard.writeText(pixData.pixCode)
      setPixCopied(true)
      setTimeout(() => setPixCopied(false), 3000)
    } catch {
      const ta = document.createElement('textarea')
      ta.value = pixData.pixCode
      document.body.appendChild(ta)
      ta.select()
      document.execCommand('copy')
      document.body.removeChild(ta)
      setPixCopied(true)
      setTimeout(() => setPixCopied(false), 3000)
    }
  }

  const handleBaixarRecibo = async (mensalidade) => {
    try {
      await baixarRecibo({
        nomeEmpresa: dados.empresa.nome,
        nomeCliente: dados.devedor.nome,
        valor: mensalidade.valor,
        dataVencimento: mensalidade.data_vencimento,
        dataPagamento: mensalidade.updated_at,
        formaPagamento: mensalidade.forma_pagamento || 'PIX',
        chavePix: dados.empresa.chave_pix
      })
    } catch (error) {
      console.error('Erro ao gerar recibo:', error)
      alert('Erro ao gerar recibo. Tente novamente.')
    }
  }

  const formatarValor = (valor) => {
    return parseFloat(valor).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
  }

  const formatarData = (data) => {
    if (!data) return '-'
    const d = new Date(data + 'T00:00:00')
    return d.toLocaleDateString('pt-BR')
  }

  const getStatusInfo = (mensalidade) => {
    if (mensalidade.status === 'pago') return { label: 'Pago', color: '#16a34a', bg: '#f0fdf4', icon: 'mdi:check-circle' }
    const hoje = new Date()
    hoje.setHours(0, 0, 0, 0)
    const venc = new Date(mensalidade.data_vencimento + 'T00:00:00')
    const diffDias = Math.floor((hoje - venc) / (1000 * 60 * 60 * 24))
    if (venc < hoje) return { label: 'Atrasado', color: '#dc2626', bg: '#fef2f2', icon: 'mdi:alert-circle', diasAtraso: diffDias }
    if (venc.getTime() === hoje.getTime()) return { label: 'Vence hoje', color: '#f59e0b', bg: '#fffbeb', icon: 'mdi:clock-alert' }
    return { label: 'Em aberto', color: '#3b82f6', bg: '#eff6ff', icon: 'mdi:clock-outline' }
  }

  // Loading
  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8fafc' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: 48, height: 48, borderRadius: '50%',
            border: '3px solid #e5e7eb', borderTopColor: '#16a34a',
            animation: 'spin 0.8s linear infinite', margin: '0 auto 16px'
          }} />
          <p style={{ margin: 0, color: '#6b7280', fontSize: 14 }}>Carregando portal...</p>
          <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
        </div>
      </div>
    )
  }

  // Erro
  if (erro) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8fafc', padding: 20 }}>
        <div style={{ textAlign: 'center', maxWidth: 400 }}>
          <div style={{
            width: 72, height: 72, borderRadius: '50%', background: '#fef2f2',
            display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px'
          }}>
            <Icon icon="mdi:link-off" width="36" style={{ color: '#dc2626' }} />
          </div>
          <h2 style={{ margin: '0 0 8px', fontSize: 22, fontWeight: 700, color: '#1f2937' }}>Link invalido</h2>
          <p style={{ margin: 0, color: '#6b7280', fontSize: 15, lineHeight: 1.5 }}>{erro}</p>
        </div>
      </div>
    )
  }

  const hoje = new Date(); hoje.setHours(0, 0, 0, 0)
  
  // Ordenar mensalidades por data de vencimento (mais próxima primeiro)
  const mensalidadesOrdenadas = [...dados.mensalidades].sort((a, b) => {
    return new Date(a.data_vencimento) - new Date(b.data_vencimento)
  })
  
  const pendentes = mensalidadesOrdenadas.filter(m => m.status !== 'pago')
  const pagas = mensalidadesOrdenadas.filter(m => m.status === 'pago').reverse() // Pagas mais recentes primeiro

  const temAtrasadas = pendentes.some(m => new Date(m.data_vencimento + 'T00:00:00') < hoje)
  const inicialEmpresa = (dados.empresa.nome || 'E').charAt(0).toUpperCase()
  const inicialCliente = (dados.devedor.nome || 'C').charAt(0).toUpperCase()
  const temInfoEmpresa = dados.empresa.cnpj || dados.empresa.endereco || dados.empresa.telefone

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc' }}>
      <style>{`
        @keyframes spin { to { transform: rotate(360deg) } }
        @keyframes expandIn { from { max-height: 0; opacity: 0 } to { max-height: 700px; opacity: 1 } }
      `}</style>

      <div style={{ padding: '20px 16px 12px', textAlign: 'center' }}>
        <img
          src="/icone.svg"
          alt="ClickPag"
          style={{ height: 32, width: 'auto', marginBottom: 6 }}
        />
        <div style={{
          fontSize: 12,
          color: '#9ca3af',
          letterSpacing: '0.12em',
          textTransform: 'uppercase',
          fontWeight: 600
        }}>
          Portal do cliente
        </div>
      </div>

      <div style={{
        maxWidth: isDesktop ? 960 : 480,
        margin: '0 auto',
        padding: isDesktop ? '0 24px 40px' : '0 16px 32px',
        display: 'flex',
        flexDirection: isDesktop ? 'row' : 'column',
        alignItems: 'stretch',
        gap: isDesktop ? 24 : 16
      }}>

        <div style={{
          background: '#ffffff',
          borderRadius: 20,
          padding: 20,
          border: '1px solid #e2e8f0',
          boxShadow: '0 10px 30px rgba(15,23,42,0.04)',
          flex: isDesktop ? '0 0 40%' : '0 0 auto'
        }}>
          <div style={{
            fontSize: 12,
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.12em',
            color: '#9ca3af',
            marginBottom: 14
          }}>
            Dados do cadastro
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              {dados.empresa.logo_url ? (
                <div style={{
                  width: 72,
                  height: 72,
                  borderRadius: 16,
                  background: '#ffffff',
                  overflow: 'hidden',
                  border: '1px solid #e2e8f0',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  <img
                    src={dados.empresa.logo_url}
                    alt={dados.empresa.nome}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                </div>
              ) : (
                <div style={{
                  width: 72,
                  height: 72,
                  borderRadius: 16,
                  background: '#f0fdf4',
                  border: '1px solid #dcfce7',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 28,
                  fontWeight: 800,
                  color: '#16a34a',
                  flexShrink: 0
                }}>
                  {inicialEmpresa}
                </div>
              )}
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                  Empresa
                </div>
                <div style={{ fontSize: 18, fontWeight: 700, color: '#0f172a', marginTop: 2, marginBottom: 4 }}>
                  {dados.empresa.nome}
                </div>
                {temInfoEmpresa && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, color: '#64748b' }}>
                    {dados.empresa.cnpj && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <Icon icon="mdi:card-account-details-outline" width="14" style={{ color: '#94a3b8', flexShrink: 0 }} />
                        {dados.empresa.cnpj}
                      </div>
                    )}
                    {dados.empresa.endereco && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <Icon icon="mdi:map-marker-outline" width="14" style={{ color: '#94a3b8', flexShrink: 0 }} />
                        <span>{dados.empresa.endereco}</span>
                      </div>
                    )}
                    {dados.empresa.telefone && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <Icon icon="mdi:phone-outline" width="14" style={{ color: '#94a3b8', flexShrink: 0 }} />
                        {dados.empresa.telefone}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div style={{ height: 1, background: '#f1f5f9' }} />

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  background: '#eff6ff',
                  border: '1px solid #dbeafe',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 16,
                  fontWeight: 700,
                  color: '#1d4ed8',
                  flexShrink: 0
                }}>
                  {inicialCliente}
                </div>
                <div>
                  <div style={{
                    fontSize: 11,
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.08em',
                    color: '#9ca3af'
                  }}>
                    Cliente
                  </div>
                  <div style={{ fontSize: 15, color: '#334155', fontWeight: 600, marginTop: 2 }}>
                    {dados.devedor.nome}
                  </div>
                </div>
              </div>
              {dados.devedor.plano_nome ? (
                <span style={{
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                  background: '#f0fdf4', color: '#16a34a',
                  padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600
                }}>
                  <Icon icon="mdi:star" width="13" />
                  {dados.devedor.plano_nome}
                </span>
              ) : pendentes.length > 0 ? (
                <div style={{
                  fontSize: 12, color: temAtrasadas ? '#dc2626' : '#64748b', fontWeight: 600,
                  display: 'flex', alignItems: 'center', gap: 4
                }}>
                  <Icon
                    icon={temAtrasadas ? 'mdi:alert-circle-outline' : 'mdi:receipt-text-clock-outline'}
                    width="16"
                    style={{ color: temAtrasadas ? '#dc2626' : '#16a34a' }}
                  />
                  {pendentes.length} {pendentes.length === 1 ? 'pendente' : 'pendentes'}
                </div>
              ) : (
                <div style={{ fontSize: 12, color: '#16a34a', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}>
                  <Icon icon="mdi:check-circle" width="16" />
                  Cadastro em dia
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Card do Contrato - aparece apenas se tiver contrato */}
        {dados.contrato && (
          <div style={{
            background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)',
            borderRadius: 20,
            padding: 20,
            boxShadow: '0 10px 30px rgba(30,64,175,0.2)',
            flex: isDesktop ? '1 1 60%' : '0 0 auto',
            color: '#ffffff'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
              <div style={{
                width: 32, height: 32, borderRadius: 8,
                background: 'rgba(255,255,255,0.2)',
                display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}>
                <Icon icon="mdi:file-document-outline" width="18" style={{ color: '#ffffff' }} />
              </div>
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', opacity: 0.9 }}>
                  Contrato Ativo
                </div>
                <div style={{ fontSize: 11, opacity: 0.8, marginTop: 2 }}>
                  Vigência do contrato
                </div>
              </div>
            </div>

            <div style={{ 
              background: 'rgba(255,255,255,0.1)', 
              borderRadius: 12, 
              padding: 16,
              marginBottom: 16,
              border: '1px solid rgba(255,255,255,0.2)'
            }}>
              <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>
                Período do contrato
              </div>
              <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>
                De {formatarData(dados.contrato.data_inicio)} até {dados.contrato.data_fim ? formatarData(dados.contrato.data_fim) : 'indeterminado'}
              </div>
              {dados.contrato.observacoes && (
                <div style={{ fontSize: 12, opacity: 0.9, marginTop: 8, fontStyle: 'italic' }}>
                  {dados.contrato.observacoes}
                </div>
              )}
            </div>

            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {dados.contrato.pdf_url && (
                <button
                  onClick={() => window.open(dados.contrato.pdf_url, '_blank', 'noopener,noreferrer')}
                  style={{
                    flex: 1,
                    minWidth: 140,
                    padding: '12px 16px',
                    borderRadius: 10,
                    border: '1px solid rgba(255,255,255,0.3)',
                    background: 'rgba(255,255,255,0.15)',
                    color: '#ffffff',
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 8,
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.25)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.15)'}
                >
                  <Icon icon="mdi:file-pdf-box" width="18" />
                  Ver contrato (PDF)
                </button>
              )}
              {dados.contrato.assinatura_url && (
                <button
                  onClick={() => window.open(dados.contrato.assinatura_url, '_blank', 'noopener,noreferrer')}
                  style={{
                    flex: 1,
                    minWidth: 140,
                    padding: '12px 16px',
                    borderRadius: 10,
                    border: 'none',
                    background: '#ffffff',
                    color: '#1e40af',
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 8,
                    transition: 'all 0.2s ease',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.background = '#f0f9ff'
                    e.currentTarget.style.transform = 'translateY(-1px)'
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.background = '#ffffff'
                    e.currentTarget.style.transform = 'translateY(0)'
                  }}
                >
                  <Icon icon="mdi:draw-pen" width="18" />
                  Assinar contrato
                </button>
              )}
            </div>
          </div>
        )}

        <div style={{
          background: '#ffffff',
          borderRadius: 20,
          padding: 20,
          border: '1px solid #e2e8f0',
          boxShadow: '0 10px 30px rgba(15,23,42,0.04)',
          flex: isDesktop ? '1 1 60%' : '0 0 auto'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{
                width: 28, height: 28, borderRadius: 999,
                background: '#ecfdf3', display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}>
                <Icon icon="mdi:receipt-text-outline" width="16" style={{ color: '#16a34a' }} />
              </div>
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#0f172a', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                  Faturas e pagamentos
                </div>
                <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>
                  Veja suas mensalidades e recibos
                </div>
              </div>
            </div>
            {pendentes.length > 0 ? (
              <span style={{
                fontSize: 11, fontWeight: 600,
                padding: '4px 10px', borderRadius: 999,
                background: temAtrasadas ? '#fef2f2' : '#eff6ff',
                color: temAtrasadas ? '#dc2626' : '#1d4ed8'
              }}>
                {pendentes.length} {pendentes.length === 1 ? 'pendente' : 'pendentes'}
              </span>
            ) : (
              <span style={{
                fontSize: 11, fontWeight: 600,
                padding: '4px 10px', borderRadius: 999,
                background: '#f0fdf4', color: '#16a34a',
                display: 'inline-flex', alignItems: 'center', gap: 4
              }}>
                <Icon icon="mdi:check-decagram" width="14" />
                Tudo em dia
              </span>
            )}
          </div>

          {pendentes.length === 0 && (
            <div style={{
              marginTop: 12,
              padding: '14px 12px',
              borderRadius: 14,
              background: '#f0fdf4',
              border: '1px dashed #bbf7d0',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: 18, fontWeight: 700, color: '#166534', marginBottom: 4 }}>
                Nenhuma mensalidade pendente
              </div>
              <div style={{ fontSize: 12, color: '#16a34a' }}>
                Quando houver uma nova cobrança, ela aparece aqui para pagamento
              </div>
            </div>
          )}

          {pendentes.length > 0 && (
            <div style={{ marginTop: 12 }}>
              {pendentes.map(m => {
                const info = getStatusInfo(m)
                const isExpanded = expandedId === m.id
                return (
                  <div key={m.id} style={{
                    background: '#ffffff', borderRadius: 14,
                    marginBottom: 12, border: isExpanded ? '1px solid #d1d5db' : '1px solid #e2e8f0',
                    boxShadow: isExpanded ? '0 4px 20px rgba(0,0,0,0.08)' : '0 1px 2px rgba(0,0,0,0.03)',
                    transition: 'box-shadow 0.2s ease, border-color 0.2s ease',
                    overflow: 'hidden'
                  }}>
                    <div style={{ padding: 20 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 }}>
                        <div style={{ fontSize: 22, fontWeight: 800, color: '#0f172a', letterSpacing: '-0.5px' }}>
                          {formatarValor(m.valor)}
                        </div>
                        <span style={{
                          padding: '5px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600,
                          color: info.color, backgroundColor: info.bg,
                          display: 'flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap'
                        }}>
                          <Icon icon={info.icon} width="14" />
                          {info.label}
                        </span>
                      </div>

                      <div style={{ fontSize: 13, color: '#64748b', display: 'flex', alignItems: 'center', gap: 4, marginTop: 2 }}>
                        <Icon icon="mdi:calendar-outline" width="14" />
                        Vencimento: {formatarData(m.data_vencimento)}
                      </div>

                      {info.diasAtraso > 0 && (
                        <div style={{
                          fontSize: 12, color: '#dc2626', fontWeight: 600, marginTop: 4,
                          display: 'flex', alignItems: 'center', gap: 4
                        }}>
                          <Icon icon="mdi:clock-alert-outline" width="14" />
                          {info.diasAtraso} {info.diasAtraso === 1 ? 'dia' : 'dias'} de atraso
                        </div>
                      )}

                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 16 }}>
                        <button
                          onClick={() => handlePagar(m)}
                          disabled={pagandoId === m.id}
                          style={{
                            width: '100%', padding: '14px 16px', borderRadius: 12,
                            border: isExpanded ? '1px solid #e2e8f0' : 'none',
                            cursor: pagandoId === m.id ? 'wait' : 'pointer',
                            background: isExpanded
                              ? '#f8fafc'
                              : 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)',
                            color: isExpanded ? '#64748b' : '#ffffff',
                            fontSize: 15, fontWeight: 700,
                            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                            opacity: pagandoId === m.id ? 0.7 : 1,
                            transition: 'all 0.2s ease',
                            boxShadow: isExpanded ? 'none' : '0 2px 8px rgba(22, 163, 74, 0.3)'
                          }}
                        >
                          {pagandoId === m.id ? (
                            <>
                              <div style={{
                                width: 18, height: 18, borderRadius: '50%',
                                border: '2px solid rgba(255,255,255,0.3)', borderTopColor: 'white',
                                animation: 'spin 0.8s linear infinite'
                              }} />
                              Gerando pagamento...
                            </>
                          ) : isExpanded ? (
                            <>
                              <Icon icon="mdi:chevron-up" width="18" />
                              Fechar
                            </>
                          ) : (
                            <>
                              <Icon icon="mdi:qrcode" width="18" />
                              Pagar agora
                            </>
                          )}
                        </button>

                        {m.boleto?.invoice_url && (
                          <button
                            type="button"
                            onClick={() => window.open(m.boleto.invoice_url, '_blank', 'noopener,noreferrer')}
                            style={{
                              width: '100%',
                              padding: '10px 12px',
                              borderRadius: 10,
                              border: '1px dashed #cbd5e1',
                              background: '#f8fafc',
                              cursor: 'pointer',
                              fontSize: 12,
                              color: '#0f172a',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: 6
                            }}
                          >
                            <Icon icon="mdi:open-in-new" width="14" />
                            Ver fatura completa
                          </button>
                        )}

                        {!isExpanded && (
                          <div style={{
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            gap: 4, fontSize: 11, color: '#94a3b8'
                          }}>
                            <Icon icon="mdi:shield-lock-outline" width="12" />
                            Pagamento seguro
                          </div>
                        )}
                      </div>
                    </div>

                    {isExpanded && pixData && pixData.pixCode && (
                      <div style={{
                        borderTop: '1px solid #e2e8f0',
                        padding: '24px 20px',
                        background: '#fafffe',
                        animation: 'expandIn 0.3s ease-out',
                        textAlign: 'center'
                      }}>
                        <div style={{
                          display: 'inline-flex', alignItems: 'center', gap: 6,
                          background: '#f0fdf4', border: '1px solid #dcfce7',
                          padding: '8px 16px', borderRadius: 24,
                          fontSize: 13, fontWeight: 600, color: '#16a34a', marginBottom: 20
                        }}>
                          <Icon icon="mdi:shield-check-outline" width="16" />
                          Pagamento seguro via PIX
                        </div>

                        <div style={{
                          background: '#ffffff', borderRadius: 16, padding: 24,
                          display: 'inline-block', border: '2px solid #e2e8f0',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
                        }}>
                          {pixData.qrImage ? (
                            <img
                              src={`data:image/png;base64,${pixData.qrImage}`}
                              alt="QR Code PIX"
                              style={{ width: 200, height: 200 }}
                            />
                          ) : (
                            <QRCodeSVG value={pixData.pixCode} size={200} />
                          )}
                        </div>

                        <div style={{ fontSize: 20, fontWeight: 800, color: '#0f172a', marginTop: 16, marginBottom: 4 }}>
                          {formatarValor(m.valor)}
                        </div>
                        <div style={{ fontSize: 13, color: '#64748b', marginBottom: 20 }}>
                          Escaneie o QR Code ou copie o codigo abaixo
                        </div>

                        <div style={{
                          background: '#ffffff', borderRadius: 12, padding: '16px 18px',
                          marginBottom: 16, border: '1px solid #e2e8f0', textAlign: 'left'
                        }}>
                          <div style={{ fontSize: 12, fontWeight: 700, color: '#0f172a', marginBottom: 12 }}>
                            Como pagar:
                          </div>
                          {[
                            { num: '1', text: 'Abra o app do seu banco' },
                            { num: '2', text: 'Escolha Pagar com PIX' },
                            { num: '3', text: 'Escaneie o QR Code ou cole o codigo' }
                          ].map(step => (
                            <div key={step.num} style={{
                              display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8
                            }}>
                              <div style={{
                                width: 24, height: 24, borderRadius: '50%',
                                background: '#16a34a', color: '#ffffff',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontSize: 12, fontWeight: 700, flexShrink: 0
                              }}>
                                {step.num}
                              </div>
                              <span style={{ fontSize: 13, color: '#334155', fontWeight: 500 }}>{step.text}</span>
                            </div>
                          ))}
                        </div>

                        <button
                          onClick={copiarPix}
                          style={{
                            width: '100%', padding: '15px 16px', borderRadius: 12,
                            border: 'none', cursor: 'pointer',
                            background: pixCopied
                              ? 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)'
                              : '#0f172a',
                            color: '#ffffff', fontSize: 15, fontWeight: 700,
                            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                            transition: 'all 0.2s ease'
                          }}
                        >
                          <Icon icon={pixCopied ? 'mdi:check-circle' : 'mdi:content-copy'} width="18" />
                          {pixCopied ? 'Codigo copiado!' : 'Copiar codigo PIX'}
                        </button>
                      </div>
                    )}

                    {isExpanded && pixData && !pixData.pixCode && pixData.invoiceUrl && (
                      <div style={{
                        borderTop: '1px solid #e2e8f0',
                        padding: '24px 20px',
                        background: '#fafffe',
                        textAlign: 'center',
                        animation: 'expandIn 0.3s ease-out'
                      }}>
                        <Icon icon="mdi:open-in-new" width="32" style={{ color: '#3b82f6', marginBottom: 10 }} />
                        <div style={{ fontSize: 15, fontWeight: 700, color: '#0f172a', marginBottom: 4 }}>
                          Pagamento aberto em nova aba
                        </div>
                        <div style={{ fontSize: 13, color: '#64748b', marginBottom: 16 }}>
                          Conclua o pagamento na pagina que foi aberta
                        </div>
                        <button
                          onClick={() => window.open(pixData.invoiceUrl, '_blank')}
                          style={{
                            padding: '12px 24px', borderRadius: 10,
                            border: '1px solid #e2e8f0', cursor: 'pointer',
                            background: '#ffffff', color: '#334155', fontSize: 14, fontWeight: 600,
                            display: 'inline-flex', alignItems: 'center', gap: 8,
                            boxShadow: '0 1px 2px rgba(0,0,0,0.04)'
                          }}
                        >
                          <Icon icon="mdi:open-in-new" width="16" />
                          Abrir pagamento novamente
                        </button>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )}

          {pagas.length > 0 && (
            <div style={{ marginTop: 20 }}>
              <button
                onClick={() => setMostrarPagos(!mostrarPagos)}
                style={{
                  width: '100%', padding: '12px 14px',
                  background: '#f8fafc', borderRadius: 12,
                  border: '1px solid #e2e8f0',
                  cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  transition: 'all 0.2s ease'
                }}
              >
                <span style={{
                  fontSize: 13, fontWeight: 600, color: '#334155',
                  display: 'flex', alignItems: 'center', gap: 8
                }}>
                  <Icon icon="mdi:receipt-text-check-outline" width="18" style={{ color: '#16a34a' }} />
                  Historico de pagamentos
                  <span style={{
                    background: '#f0fdf4', color: '#16a34a',
                    padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 700
                  }}>
                    {pagas.length}
                  </span>
                </span>
                <Icon
                  icon={mostrarPagos ? 'mdi:chevron-up' : 'mdi:chevron-down'}
                  width="20" style={{ color: '#94a3b8' }}
                />
              </button>

              {mostrarPagos && (
                <div style={{ marginTop: 8, borderRadius: 12, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                  {pagas.map(m => (
                    <div key={m.id} style={{
                      padding: '12px 14px',
                      borderTop: '1px solid #f1f5f9',
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12,
                      background: '#ffffff'
                    }}>
                      <div>
                        <div style={{ fontSize: 15, fontWeight: 700, color: '#0f172a' }}>
                          {formatarValor(m.valor)}
                        </div>
                        <div style={{ fontSize: 12, color: '#94a3b8', display: 'flex', alignItems: 'center', gap: 4, marginTop: 2 }}>
                          <Icon icon="mdi:calendar-check-outline" width="13" />
                          Vencimento: {formatarData(m.data_vencimento)}
                        </div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{
                          padding: '4px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600,
                          color: '#16a34a', backgroundColor: '#f0fdf4'
                        }}>
                          Pago
                        </span>
                        {m.boleto?.invoice_url && (
                          <button
                            onClick={() => window.open(m.boleto.invoice_url, '_blank', 'noopener,noreferrer')}
                            title="Ver fatura"
                            style={{
                              background: '#f8fafc', border: '1px solid #e2e8f0', cursor: 'pointer',
                              borderRadius: 8, padding: '6px 10px', display: 'flex',
                              alignItems: 'center', gap: 4, fontSize: 11, color: '#334155'
                            }}
                          >
                            <Icon icon="mdi:open-in-new" width="14" />
                            Fatura
                          </button>
                        )}
                        <button
                          onClick={() => handleBaixarRecibo(m)}
                          title="Baixar recibo"
                          style={{
                            background: '#f1f5f9', border: 'none', cursor: 'pointer',
                            borderRadius: 8, padding: 8, display: 'flex',
                            transition: 'background 0.15s ease'
                          }}
                          onMouseEnter={e => e.currentTarget.style.background = '#e2e8f0'}
                          onMouseLeave={e => e.currentTarget.style.background = '#f1f5f9'}
                        >
                          <Icon icon="mdi:download" width="18" style={{ color: '#64748b' }} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Footer minimo */}
      <div style={{ textAlign: 'center', padding: '32px 16px 48px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, marginBottom: 6 }}>
          <Icon icon="mdi:shield-lock-outline" width="14" style={{ color: '#cbd5e1' }} />
          <span style={{ fontSize: 12, color: '#cbd5e1', fontWeight: 500 }}>Portal seguro de pagamentos</span>
        </div>
        <a
          href="/?utm_source=portal_cliente&utm_medium=powered_by&utm_campaign=portal_footer"
          target="_blank"
          rel="noopener noreferrer"
          style={{
            fontSize: 13,
            color: '#94a3b8',
            marginTop: 4,
            textDecoration: 'none',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            fontWeight: 600
          }}
        >
          <Icon icon="mdi:launch" width="14" style={{ color: '#94a3b8' }} />
          <span>Powered by ClickPag</span>
        </a>
      </div>
    </div>
  )
}
